import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Mic as MicIcon, Videocam as VideocamIcon } from "@mui/icons-material";

interface Props {
  displayName: string;
  joining: boolean;
  errorMessage?: string;
  onJoin: () => void;
  onCancel: () => void;
}

/**
 * Shown before the Zoom session is started. The "Join meeting" button is the
 * user gesture that lets the browser allow getUserMedia inside the iframe —
 * relying on the parent window's gesture is unreliable.
 */
export default function PreflightScreen({
  displayName,
  joining,
  errorMessage,
  onJoin,
  onCancel,
}: Props) {
  return (
    <Stack
      sx={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        bgcolor: "background.paper",
        color: "text.primary",
        p: 4,
        textAlign: "center",
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          bgcolor: "action.hover",
          color: "text.primary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 36,
        }}
      >
        {displayName?.[0]?.toUpperCase() ?? "?"}
      </Box>
      <Box>
        <Typography variant="h5" gutterBottom>
          Ready to join?
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          Joining as <b>{displayName}</b>. The browser will ask for camera and
          microphone access.
        </Typography>
      </Box>

      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          color: "text.secondary",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <MicIcon fontSize="small" />
          <Typography
            sx={{
              fontSize: 13,
            }}
          >
            Microphone
          </Typography>
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <VideocamIcon fontSize="small" />
          <Typography
            sx={{
              fontSize: 13,
            }}
          >
            Camera
          </Typography>
        </Stack>
      </Stack>

      {errorMessage && (
        <Typography
          sx={{
            color: "error.main",
            fontSize: 13,
          }}
        >
          {errorMessage}
        </Typography>
      )}

      <Stack
        direction="row"
        sx={{
          gap: 1.5,
        }}
      >
        <Button variant="outlined" onClick={onCancel} disabled={joining}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onJoin}
          disabled={joining}
          startIcon={joining ? <CircularProgress size={16} /> : undefined}
        >
          {joining ? "Joining…" : "Join meeting"}
        </Button>
      </Stack>
    </Stack>
  );
}
